import {
    AfterViewInit,
    Directive,
    ElementRef,
    EnvironmentInjector,
    Injectable,
    InjectionToken,
    Injector,
    Type,
    createComponent,
    inject,
    reflectComponentType,
} from '@angular/core';

export const DYNAMIC_COMPONENT = new InjectionToken<{
    selector: string;
    componentType: Type<any>;
}>('component');

@Injectable()
export class DynamicComponentsLoaderService {
    private components = new Map<string, Type<any>>();

    private providedComponents =
        inject<
            Array<{
                selector: string;
                componentType: Type<any>;
            }>
        >(DYNAMIC_COMPONENT, { optional: true }) ?? [];

    allComponents = () => {
        let componentList = [];
        for (let [key, value] of this.components.entries()) {
            componentList.push({ selector: key, componentType: value });
        }
        return componentList;
    };

    constructor() {
        this.providedComponents.forEach((x) => this.register(x.selector, x.componentType));
    }

    register(selector: string, componentClass: Type<any>) {
        this.components.set(selector, componentClass);
    }

    get(selector: string) {
        return this.components.get(selector);
    }
}

@Directive({
    standalone: true,
    selector: '[msRenderDynamic]',
})
export class RenderDynamicDirective implements AfterViewInit {
    elementRef = inject(ElementRef);
    injector = inject(Injector);
    envInjector = inject(EnvironmentInjector);

    dynamicComponents = inject(DynamicComponentsLoaderService);

    ngAfterViewInit() {
        const allComponents = this.dynamicComponents.allComponents();

        allComponents.forEach((component) => {
            const element = this.elementRef.nativeElement as HTMLElement;
            const foundElements = Array.from(element.querySelectorAll(component.selector));

            for (let elementHost of foundElements) {
                if (elementHost) {
                    const cmp = createComponent(component.componentType, {
                        hostElement: elementHost,
                        elementInjector: this.injector,
                        environmentInjector: this.envInjector,
                    });

                    const cmpMirror = reflectComponentType(component.componentType);

                    Array.from(elementHost.attributes).forEach((attribute) => {
                        if (cmpMirror && attribute.name !== 'ng-version') {
                            const inputExists = cmpMirror.inputs.some((x) => x.propName === attribute.name);
                            if (inputExists) {
                                cmp.setInput(attribute.name, attribute.value);
                            }
                        }
                    });

                    cmp.changeDetectorRef.detectChanges();
                }
            }
        });
    }
}

export function provideDynamicComponents(components: Array<{ selector: string; componentType: Type<any> }>) {
    return [
        DynamicComponentsLoaderService,
        ...components.map((cmp) => {
            return {
                provide: DYNAMIC_COMPONENT,
                useValue: {
                    selector: cmp.selector,
                    componentType: cmp.componentType,
                },
                multi: true,
            };
        }),
    ];
}
